/* tslint:disable */

/**
 * Shamelessly stole code from https://github.com/frankwallis/react-slidedown because
 * the lib doesn't work with our version of React and because WebKit can't be bothered to
 * get their act together and fix transitions for auto-height https://bugs.webkit.org/show_bug.cgi?id=16020
 *
 * Changed CSS classes to match our conventions but otherwise it's the same code from there.
 *
 * LICENSE:
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2017 Frank Wallis
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 */
import cx from "classnames";
import React from "react";
import { forwardRef, Component } from "react";
import "./SlideDown.scss";

interface ISlideDownContentProps extends ISlideDownProps {
  forwardedRef: React.Ref<HTMLDivElement> | null;
}

interface ISlideDownContentState {
  children: React.ReactNode;
  childrenLeaving: boolean;
}

class SlideDownContent extends Component<
  ISlideDownContentProps,
  ISlideDownContentState
> {
  public static defaultProps = {
    transitionOnAppear: true,
    closed: false,
  };

  private outerRef: HTMLDivElement | null = null;

  constructor(props: ISlideDownContentProps) {
    super(props);

    this.state = {
      children: props.children,
      childrenLeaving: false,
    };
  }

  private handleRef = (ref: HTMLDivElement | null) => {
    /* Handle both the internal and forwardedRef and maintain correct typings */
    this.outerRef = ref;

    if (this.props.forwardedRef) {
      if (typeof this.props.forwardedRef === "function") {
        this.props.forwardedRef(ref);
      } else if (typeof this.props.forwardedRef === "object") {
        const forwardedRef = this.props.forwardedRef as any;
        forwardedRef.current = ref;
      } else {
        throw new Error(`Invalid forwardedRef ${this.props.forwardedRef}`);
      }
    }
  };

  public componentDidMount() {
    if (this.outerRef) {
      if (this.props.closed || !this.props.children) {
        this.outerRef.classList.add("c-slide-down__closed");
        this.outerRef.style.height = "0px";
      } else if (this.props.transitionOnAppear) {
        this.startTransition("0px");
      } else {
        this.outerRef.style.height = "auto";
      }
    }
  }

  public getSnapshotBeforeUpdate() {
    /* Prepare to resize */
    return this.outerRef
      ? this.outerRef.getBoundingClientRect().height + "px"
      : null;
  }

  public static getDerivedStateFromProps(
    props: ISlideDownContentProps,
    state: ISlideDownContentState
  ) {
    if (props.children) {
      return {
        children: props.children,
        childrenLeaving: false,
      };
    } else if (state.children) {
      return {
        children: state.children,
        childrenLeaving: true,
      };
    } else {
      return null;
    }
  }

  // @ts-ignore
  public componentDidUpdate(prevProps, prevState, snapshot: string | null) {
    if (this.outerRef) {
      this.startTransition(snapshot);
    }
  }

  private startTransition(prevHeight: string | null) {
    let endHeight: string | null = "0px";

    if (this.outerRef && prevHeight) {
      if (
        !this.props.closed &&
        !this.state.childrenLeaving &&
        this.state.children
      ) {
        this.outerRef.classList.remove("c-slide-down__closed");
        this.outerRef.style.height = "auto";
        endHeight = getComputedStyle(this.outerRef).height;
      }

      if (
        endHeight &&
        parseFloat(endHeight).toFixed(2) !== parseFloat(prevHeight).toFixed(2)
      ) {
        this.outerRef.classList.add("c-slide-down__transitioning");
        this.outerRef.style.height = prevHeight;
        this.outerRef.offsetHeight; // force repaint
        this.outerRef.style.transitionProperty = "height";
        this.outerRef.style.height = endHeight;
      }
    }
  }

  private endTransition() {
    if (this.outerRef) {
      this.outerRef.classList.remove("c-slide-down__transitioning");
      this.outerRef.style.transitionProperty = "none";
      this.outerRef.style.height = this.props.closed ? "0px" : "auto";

      if (this.props.closed || !this.state.children) {
        this.outerRef.classList.add("c-slide-down__closed");
      }
    }
  }

  private handleTransitionEnd = (evt: React.TransitionEvent) => {
    if (evt.target === this.outerRef && evt.propertyName === "height") {
      if (this.state.childrenLeaving) {
        this.setState({ children: null, childrenLeaving: false }, () =>
          this.endTransition()
        );
      } else {
        this.endTransition();
      }
    }
  };

  public render() {
    const {
      children,
      className,
      closed,
      transitionOnAppear,
      forwardedRef,
      ...rest
    } = this.props;
    const containerClassName = cx("c-slide-down", className);

    return (
      <div
        ref={this.handleRef}
        className={containerClassName}
        onTransitionEnd={this.handleTransitionEnd}
        {...rest}
      >
        {this.state.children}
      </div>
    );
  }
}

export interface ISlideDownProps extends React.HTMLAttributes<HTMLDivElement> {
  closed?: boolean;
  transitionOnAppear?: boolean;
}

export const SlideDown = forwardRef(
  (props: ISlideDownProps, ref: React.Ref<HTMLDivElement>) => (
    <SlideDownContent {...props} forwardedRef={ref} />
  )
);

export default SlideDown;
